"use client";
import React from "react";
import {
  Box,
  Circle,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import Image, {StaticImageData} from "next/image";
import {IconType} from "@onemind-services-llc/react-icons-ng";
import {HEADING_VARIANTS} from "@/theme/components/heading";
import {
  useTextVariants,
  useSectionHeadingVariant,
} from "@/theme/components/useSectionHeading";
import SeparatorImg from "public/shared/separator.png";

type Props = {
  TITLE: string;
  ITEMS: {
    image?: StaticImageData;
    icon?: IconType;
    title: string;
    content: string;
  }[];
  SEPARATOR_IMG?: StaticImageData;
  CARD_HEIGHT?: string;
};

const KeyFeaturesComponent = (props: Props) => {
  const {TITLE, ITEMS, SEPARATOR_IMG = SeparatorImg, CARD_HEIGHT = "176px"} = props;
  const midpoint = Math.ceil(ITEMS.length / 2);
  const list1 = ITEMS.slice(0, midpoint);
  const list2 = ITEMS.slice(midpoint);
  const HeadingVariant = useSectionHeadingVariant();
  const TextVariant = useTextVariants();
  return (
    <Box>
      <Heading as="h1" variant={HeadingVariant} textAlign={"center"} mb={4}>
        {TITLE}
      </Heading>
      <SimpleGrid
        gridTemplateColumns={{
          base: "1fr",
          sm: "1fr 1fr",
          md: "1fr 0.25fr 1fr",
        }}
        spacing={{base: 6, sm: 8}}
        paddingX={"10px"}
        data-cy={"section-features"}
      >
        <VStack align={{base: "center", sm: "end"}} spacing={10} py={10}>
          {list1.map((item, index) => {
            const {image, icon, title, content} = item;
            return (
              <Stack
                key={index}
                backgroundColor={"#F9FAFC"}
                borderRadius={"8px"}
                p={4}
                spacing={6}
                w={{base: "auto", sm: "auto", md: "348px"}}
                h={{base: "auto", sm: CARD_HEIGHT}}
                boxShadow={"lg"}
                borderWidth={"1px"}
                _hover={{
                  borderColor: "#9ECFFF",
                  transform: "scale(1.03)",
                  transition: "0.3s",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.3)",
                }}
              >
                <HStack spacing={4}>
                  {image && (
                    <Image
                      alt={title}
                      src={image}
                      style={{height: "auto", width: "48px"}}
                    />
                  )}
                  {icon && (
                    <Circle
                      size="60px"
                      border="2px solid"
                      borderColor="primary.200"
                      color="primary.200"
                    >
                      <Icon as={icon} w={10} h={10} />
                    </Circle>
                  )}
                  <Heading as="h2" variant={HEADING_VARIANTS.SUBTITLE1} fontWeight={600}>
                    {title}
                  </Heading>
                </HStack>
                <Text variant={TextVariant} color={"text.100"}>
                  {content}
                </Text>
              </Stack>
            );
          })}
        </VStack>
        <Stack
          display={{base: "none", md: "flex"}}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Image src={SEPARATOR_IMG} alt="Separator" style={{maxHeight: "900px"}} />
        </Stack>
        <VStack align={{base: "center", sm: "start"}} spacing={10} py={10}>
          {list2.map((item, index) => {
            const {image, icon, title, content} = item;
            return (
              <Stack
                key={index}
                backgroundColor={"#F9FAFC"}
                borderRadius={"8px"}
                p={6}
                spacing={6}
                w={{base: "full", sm: "auto", md: "348px"}}
                h={{base: "auto", sm: CARD_HEIGHT}}
                boxShadow={"lg"}
                borderWidth={"1px"}
                _hover={{
                  borderColor: "#9ECFFF",
                  transform: "scale(1.03)",
                  transition: "0.3s",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.3)",
                }}
              >
                <HStack spacing={4}>
                  {image && (
                    <Image
                      alt={title}
                      src={image}
                      style={{height: "auto", width: "48px"}}
                    />
                  )}
                  {icon && (
                    <Circle
                      size="60px"
                      border="2px solid"
                      borderColor="primary.200"
                      color="primary.200"
                    >
                      <Icon as={icon} w={10} h={10} />
                    </Circle>
                  )}
                  <Heading as="h2" variant={HEADING_VARIANTS.SUBTITLE1} fontWeight={600}>
                    {title}
                  </Heading>
                </HStack>
                <Text variant={TextVariant} color={"text.100"}>
                  {content}
                </Text>
              </Stack>
            );
          })}
        </VStack>
      </SimpleGrid>
    </Box>
  );
};

export default KeyFeaturesComponent;
